import type { ConcentrationUnit, VolumeUnit } from '@/utils/calculatorMath'
import CalculatorActions from '@/components/calculators/CalculatorActions'
import ValidationAlert from '@/components/calculators/ValidationAlert'
import {
  concentrationToMolar,
  formatNumber,
  litersToVolume,
  molarToConcentration,
  volumeToLiters,
} from '@/utils/calculatorMath'
import {Lightbulb} from 'lucide-react'
import { useMemo, useState } from 'react'

const CONCENTRATION_UNITS: ConcentrationUnit[] = ['M', 'mM', 'µM']
const VOLUME_UNITS: VolumeUnit[] = ['L', 'mL', 'µL']

type FieldKey = 'c1' | 'v1' | 'c2' | 'v2'

interface FieldState {
  value: string
  unit: string
}

const INITIAL_STATE: Record<FieldKey, FieldState> = {
  c1: { value: '', unit: 'M' },
  v1: { value: '', unit: 'mL' },
  c2: { value: '', unit: 'M' },
  v2: { value: '', unit: 'mL' },
}

interface SolveResult {
  solvedField: FieldKey
  solvedValue: number
}

function MolarityDilutionCalculator() {
  const [fields, setFields] = useState<Record<FieldKey, FieldState>>(INITIAL_STATE)
  const [result, setResult] = useState<SolveResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const updateField = (key: FieldKey, patch: Partial<FieldState>) => {
    setFields(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
    setCopied(false)
  }

  const handleReset = () => {
    setFields(INITIAL_STATE)
    setResult(null)
    setError(null)
    setCopied(false)
  }

  const handleCalculate = () => {
    setCopied(false)
    const raw: Record<FieldKey, number | null> = {
      c1: fields.c1.value.trim() === '' ? null : Number(fields.c1.value),
      v1: fields.v1.value.trim() === '' ? null : Number(fields.v1.value),
      c2: fields.c2.value.trim() === '' ? null : Number(fields.c2.value),
      v2: fields.v2.value.trim() === '' ? null : Number(fields.v2.value),
    }

    for (const key of Object.keys(raw) as FieldKey[]) {
      const val = raw[key]
      if (val !== null && (Number.isNaN(val) || val < 0)) {
        setError('Please enter valid, non-negative numbers only.')
        setResult(null)
        return
      }
    }

    const missingOrZero = (Object.keys(raw) as FieldKey[]).filter(key => raw[key] === null || raw[key] === 0)

    if (missingOrZero.length !== 1) {
      setError('Leave exactly ONE field blank or set to 0, then click Calculate to solve for it.')
      setResult(null)
      return
    }

    const target = missingOrZero[0]

    const c1Molar = raw.c1 !== null ? concentrationToMolar(raw.c1, fields.c1.unit as ConcentrationUnit) : null
    const v1Liters = raw.v1 !== null ? volumeToLiters(raw.v1, fields.v1.unit as VolumeUnit) : null
    const c2Molar = raw.c2 !== null ? concentrationToMolar(raw.c2, fields.c2.unit as ConcentrationUnit) : null
    const v2Liters = raw.v2 !== null ? volumeToLiters(raw.v2, fields.v2.unit as VolumeUnit) : null

    let solvedValue: number

    try {
      if (target === 'c1') {
        if (v1Liters === null || v1Liters === 0 || c2Molar === null || v2Liters === null)
          throw new Error('Insufficient data')
        solvedValue = molarToConcentration((c2Molar * v2Liters) / v1Liters, fields.c1.unit as ConcentrationUnit)
      }
      else if (target === 'v1') {
        if (c1Molar === null || c1Molar === 0 || c2Molar === null || v2Liters === null)
          throw new Error('Insufficient data')
        solvedValue = litersToVolume((c2Molar * v2Liters) / c1Molar, fields.v1.unit as VolumeUnit)
      }
      else if (target === 'c2') {
        if (c1Molar === null || v1Liters === null || v2Liters === null || v2Liters === 0)
          throw new Error('Insufficient data')
        solvedValue = molarToConcentration((c1Molar * v1Liters) / v2Liters, fields.c2.unit as ConcentrationUnit)
      }
      else {
        if (c1Molar === null || v1Liters === null || c2Molar === null || c2Molar === 0)
          throw new Error('Insufficient data')
        solvedValue = litersToVolume((c1Molar * v1Liters) / c2Molar, fields.v2.unit as VolumeUnit)
      }
    }
    catch {
      setError('Cannot divide by zero — check that the remaining three fields are valid and non-zero.')
      setResult(null)
      return
    }

    if (!Number.isFinite(solvedValue) || solvedValue < 0) {
      setError('Calculation produced an invalid result. Please verify your inputs.')
      setResult(null)
      return
    }

    setError(null)
    setResult({ solvedField: target, solvedValue })
  }

  const resultText = useMemo(() => {
    if (!result)
      return ''
    const labelMap: Record<FieldKey, string> = { c1: 'C1', v1: 'V1', c2: 'C2', v2: 'V2' }
    return `${labelMap[result.solvedField]} = ${formatNumber(result.solvedValue)} ${fields[result.solvedField].unit}`
  }, [result, fields])

  const handleCopy = async () => {
    if (!resultText)
      return
    try {
      await navigator.clipboard.writeText(resultText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
    catch {
      setError('Unable to copy to clipboard.')
    }
  }

  const fieldConfig: { key: FieldKey, label: string, units: string[], hint: string }[] = [
    { key: 'c1', label: 'Concentration 1 (C1)', units: CONCENTRATION_UNITS, hint: 'Concentration of your starting stock solution. Leave blank to solve for C1.' },
    { key: 'v1', label: 'Volume 1 (V1)', units: VOLUME_UNITS, hint: 'Volume of stock solution needed. Leave blank to solve for V1.' },
    { key: 'c2', label: 'Concentration 2 (C2)', units: CONCENTRATION_UNITS, hint: 'Target concentration of your final diluted solution. Leave blank to solve for C2.' },
    { key: 'v2', label: 'Volume 2 (V2)', units: VOLUME_UNITS, hint: 'Final total volume you want to prepare. Leave blank to solve for V2.' },
  ]

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Molarity & Dilution Calculator</h2>
      <p className="text-sm text-slate-600 mb-4">
        Solve C1V1 = C2V2 for any missing variable. Leave exactly one field blank (or 0), then click Calculate.
      </p>

      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 mb-6">
        <Lightbulb className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">How to use this calculator: </span>
          Enter values for any three of the four fields below (C1, V1, C2, V2) and pick the correct unit for each. Leave the fourth field empty, or type 0, then press <span className="font-semibold">Calculate</span> to instantly solve for it using BioSlick's dilution formula.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {fieldConfig.map(({ key, label, units, hint }) => (
          <div key={key}>
            <label htmlFor={`md-${key}`} className="block text-sm font-semibold text-slate-800 mb-1.5">
              {label}
            </label>
            <div className="flex gap-2">
              <input
                id={`md-${key}`}
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                placeholder="Leave blank to solve"
                value={fields[key].value}
                onChange={e => updateField(key, { value: e.target.value })}
                aria-describedby={`md-${key}-hint`}
                className="flex-1 min-w-0 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
              />
              <select
                aria-label={`${label} unit`}
                value={fields[key].unit}
                onChange={e => updateField(key, { unit: e.target.value })}
                className="rounded-md border border-slate-300 px-2 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600"
              >
                {units.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <p id={`md-${key}-hint`} className="mt-1.5 text-xs text-slate-500 leading-snug">
              {hint}
            </p>
          </div>
        ))}
      </div>

      <ValidationAlert message={error} />

      <div className="flex flex-wrap gap-3 mt-4 mb-4">
        <button
          type="button"
          onClick={handleCalculate}
          className="px-5 py-2.5 rounded-md text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 transition-colors"
        >
          Calculate
        </button>
      </div>

      <div
        role="status"
        aria-live="polite"
        className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 mb-4 min-h-[76px] flex flex-col justify-center"
      >
        {result
          ? (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">Result</p>
              <p className="text-2xl font-bold text-slate-900">{resultText}</p>
            </>
          )
          : (
            <p className="text-sm text-slate-500">Your solved value will appear here.</p>
          )}
      </div>

      <CalculatorActions onReset={handleReset} onCopy={handleCopy} copied={copied} />

      <div className="mt-8 border-t border-slate-200 pt-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">How the Dilution Formula Works</h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-5">
          The dilution equation C1V1 = C2V2 expresses conservation of solute moles: the amount of solute before dilution (concentration C1 multiplied by volume V1) equals the amount after dilution (concentration C2 multiplied by volume V2), since diluting a solution changes its volume and concentration but not the total quantity of dissolved solute. BioSlick automatically converts between M, mM, µM, L, mL, and µL behind the scenes, so you never need to unit-convert by hand.
        </p>

        <h4 className="text-base font-bold text-slate-900 mb-3">Worked Examples for Every Variable</h4>
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 1 — Solving for V1 (how much stock to use)</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              You have a 5 M stock solution (C1) and need 10 mL (V2) of a 2 M working solution (C2). Leave V1 blank and click Calculate:
              <br />
              V1 = (C2 × V2) / C1 = (2 M × 10 mL) / 5 M = <span className="font-semibold text-slate-900">4 mL</span>. Combine 4 mL of stock with 6 mL of diluent to reach 10 mL at 2 M.
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 2 — Solving for C1 (required stock strength)</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              You want to prepare 50 mL (V2) of a 100 mM buffer (C2), starting from 5 mL (V1) of concentrated stock. Leave C1 blank and click Calculate:
              <br />
              C1 = (C2 × V2) / V1 = (100 mM × 50 mL) / 5 mL = <span className="font-semibold text-slate-900">1000 mM (1 M)</span>. Your stock solution must be prepared at 1 M.
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 3 — Solving for C2 (resulting concentration)</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              You dilute 2 mL (V1) of a 10 µM antibody stock (C1) up to a final volume of 20 mL (V2). Leave C2 blank and click Calculate:
              <br />
              C2 = (C1 × V1) / V2 = (10 µM × 2 mL) / 20 mL = <span className="font-semibold text-slate-900">1 µM</span>. Your final working concentration is 1 µM.
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 4 — Solving for V2 (final total volume)</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              You take 1 mL (V1) of a 20 mM DNA stock (C1) and dilute it down to 4 mM (C2). Leave V2 blank and click Calculate:
              <br />
              V2 = (C1 × V1) / C2 = (20 mM × 1 mL) / 4 mM = <span className="font-semibold text-slate-900">5 mL</span>. Bring the final volume up to 5 mL total.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MolarityDilutionCalculator
